import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { ssoDestination } from '@/lib/ssoDestination'

// SAFT-AI federated login landing. The SAFT-AI app deep-links here with a signed
// token and the company the user was viewing (`/sso?token=<JWT>&nif=<NIF>`). We hand
// the token to the `saftai-sso` edge function, which verifies it, provisions our rows,
// and returns a single-use `token_hash`; we redeem it via verifyOtp to establish a
// native Supabase session, then route by role INTO that company's context.
// Public route (no guard) — the edge function is the trust boundary.
//
// `nif` is optional and best-effort: SAFT-AI may omit it, and it may name a company this
// principal cannot see. Either way we still land them somewhere sensible (see ssoDestination).

type SsoState = 'working' | 'error'

export function SsoCallback() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [state, setState] = useState<SsoState>('working')
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return // StrictMode double-invoke + single-use token guard
    ran.current = true

    const token = params.get('token')
    if (!token) {
      setState('error')
      return
    }

    void (async () => {
      const { data, error } = await supabase.functions.invoke('saftai-sso', { body: { token } })
      const tokenHash = (data as { token_hash?: string; role?: string } | null)?.token_hash
      const role = (data as { role?: string } | null)?.role
      if (error || !tokenHash) {
        setState('error')
        return
      }
      const { error: otpErr } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'magiclink',
      })
      if (otpErr) {
        setState('error')
        return
      }

      // Resolve the company AFTER the session exists, so the lookup runs under the caller's own
      // RLS. One query serves both principal types: staff see the row only if it is in their
      // firm/carteira, a portal user only if it is one of their companies. Explicit column —
      // never select('*') on `clients` (the at_password grant revoke makes it fail).
      let clientId: string | null = null
      const nif = params.get('nif')
      if (nif) {
        const { data: match } = await supabase
          .from('clients')
          .select('id')
          .eq('nif', nif)
          .maybeSingle()
        clientId = (match as { id: string } | null)?.id ?? null
      }

      // useSession picks up the new session; route by principal into the company's context.
      navigate(ssoDestination(role, clientId), { replace: true })
    })()
  }, [params, navigate])

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-8 text-gray-700">
      <div className="rounded-lg border border-DEFAULT bg-surface px-6 py-4 text-center text-[13px] shadow-card">
        {state === 'working' ? (
          <p>A iniciar sessão…</p>
        ) : (
          <p className="text-red-700">
            Não foi possível iniciar a sessão. Volte à SAFT-AI e tente novamente.
          </p>
        )}
      </div>
    </main>
  )
}
